import {
  ArrowLeftRight,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  CircleOff,
  DownloadCloud,
  ShieldCheck,
  Store,
} from 'lucide-react'
import type { ReactNode } from 'react'

import { Button, CheckboxField, SelectField } from '@krp/ui-kit'

import { ConnectionLine } from '../../../shared/ui/ConnectionLine'
import { QueryState } from '@krp/sale-ui'
import '../../../shared/ui/aqsi.css'
import type { CatalogActions, CatalogVm } from '../model/types'

const PRESENCE_ICONS: Readonly<Record<'ok' | 'missing' | 'invalid', ReactNode>> = {
  ok: <CircleCheck size={14} aria-hidden="true" />,
  missing: <CircleOff size={14} aria-hidden="true" />,
  invalid: <CircleAlert size={14} aria-hidden="true" />,
}

/**
 * Каталог и кассы кабинета AQSI — снимки службы (C2). «Загрузить … из кабинета»
 * читает кабинет ЭТОГО rid; проверка профиля — до продажи, без эффекта. Отдельной
 * «Обновить» нет (RP-FB1): она лишь перечитывала снимок и путалась с загрузкой;
 * перечитать снимок можно «Повторить» при ошибке.
 */
export function CatalogScreen({
  vm,
  actions,
  probeSearch = null,
}: {
  vm: CatalogVm
  actions: CatalogActions
  /** RP-SE12: блок «Поиск в AQSI» (отдельный индекс единой службы поиска) — вверху окна; его состояние держит binding. */
  probeSearch?: ReactNode
}) {
  return (
    <section className="aqsi-screen" aria-labelledby="aqsi-catalog-title">
      <header className="aqsi-toolbar">
        <h2 id="aqsi-catalog-title">Каталог и кассы AQSI</h2>
      </header>
      <p className="aqsi-caption" data-testid="aqsi-catalog-source">
        <Store size={14} aria-hidden="true" /> Кабинет: {vm.source.name}
        {vm.source.how !== null && ` (${vm.source.how})`}
        {vm.source.alternatives > 1 && (
          <>
            {' '}
            <Button
              variant="ghost"
              icon={<ArrowLeftRight size={14} aria-hidden="true" />}
              onClick={actions.switchSource}
            >
              Сменить кабинет
            </Button>
          </>
        )}
      </p>

      {probeSearch}

      <section className="aqsi-form" aria-labelledby="aqsi-goods-title">
        <div className="aqsi-toolbar">
          <h3 id="aqsi-goods-title">Товары кабинета</h3>
          <Button
            icon={<DownloadCloud size={14} aria-hidden="true" />}
            busy={vm.busy === 'catalog.refresh'}
            disabled={vm.busy !== null}
            onClick={actions.refreshCatalog}
          >
            Загрузить товары из кабинета
          </Button>
        </div>
        {vm.goodsSnapshot !== '' && <p className="aqsi-caption">{vm.goodsSnapshot}</p>}
        <QueryState query={vm.goodsQuery} what="Каталог" emptyText="В снимке товаров нет" onRetry={actions.reload} />
        {vm.goods.length > 0 && (
          <>
            <table className="aqsi-table">
              <thead>
                <tr>
                  <th>Проверить</th>
                  <th>Товар</th>
                  <th>Артикул</th>
                  <th>Цена</th>
                  <th>Штрихкоды</th>
                  <th>Состояние</th>
                </tr>
              </thead>
              <tbody>
                {vm.goods.map((good) => (
                  <tr key={good.goodId}>
                    <td data-label="Проверить">
                      <CheckboxField
                        label={<span className="visually-hidden">Отметить {good.name}</span>}
                        checked={good.checked}
                        onChange={() => actions.toggleGood(good.goodId)}
                      />
                    </td>
                    <td data-label="Товар">
                      {actions.openGood === undefined ? (
                        good.name
                      ) : (
                        <button
                          type="button"
                          className="aqsi-good-link"
                          aria-label={`Открыть карточку «${good.name}» в AQSI`}
                          onClick={() => actions.openGood?.(good.goodId)}
                        >
                          {good.name}
                        </button>
                      )}
                    </td>
                    <td data-label="Артикул">{good.sku}</td>
                    <td data-label="Цена" data-numeric="">
                      {good.price}
                    </td>
                    <td data-label="Штрихкоды">{good.barcodes}</td>
                    <td data-label="Состояние">
                      <span className="aqsi-card__state">
                        {PRESENCE_ICONS[good.stateKind]}
                        {good.state}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <nav className="aqsi-card__actions" aria-label="Страницы каталога">
              <Button
                icon={<ChevronLeft size={14} aria-hidden="true" />}
                disabled={vm.page <= 0}
                onClick={() => actions.setPage(vm.page - 1)}
              >
                Назад
              </Button>
              <span>
                Страница {vm.page + 1} из {vm.pageCount} · всего {vm.total}
              </span>
              <Button
                icon={<ChevronRight size={14} aria-hidden="true" />}
                disabled={vm.page + 1 >= vm.pageCount}
                onClick={() => actions.setPage(vm.page + 1)}
              >
                Дальше
              </Button>
            </nav>
          </>
        )}
      </section>

      <section className="aqsi-form" aria-labelledby="aqsi-devices-title">
        <div className="aqsi-toolbar">
          <h3 id="aqsi-devices-title">Кассы и кассиры</h3>
          <Button
            icon={<DownloadCloud size={14} aria-hidden="true" />}
            busy={vm.busy === 'devices.refresh'}
            disabled={vm.busy !== null}
            onClick={actions.refreshDevices}
          >
            Загрузить кассы из кабинета
          </Button>
        </div>
        {vm.devicesSnapshot !== '' && <p className="aqsi-caption">{vm.devicesSnapshot}</p>}
        <QueryState query={vm.devicesQuery} what="Кассы" emptyText="В снимке касс нет" onRetry={actions.reload} />
        {vm.devices.length > 0 && (
          <ul className="aqsi-list" aria-label="Кассы кабинета">
            {vm.devices.map((device) => (
              <li
                key={device.deviceId}
                className="aqsi-card"
                data-state={device.stateKind === 'ok' ? 'free' : 'unavailable'}
              >
                <p className="aqsi-card__title">Касса {device.serial}</p>
                <span className="aqsi-card__state">
                  {PRESENCE_ICONS[device.stateKind]}
                  {device.state}
                </span>
                <p className="aqsi-card__text">
                  {device.model} · {device.shop}
                </p>
                <p className="aqsi-card__text">СНО: {device.taxSystem}</p>
                <ConnectionLine connection={device.connection} />
              </li>
            ))}
          </ul>
        )}
        <QueryState
          query={vm.cashiersQuery}
          what="Кассиры"
          emptyText="Кассиров в снимке нет"
          onRetry={actions.reload}
        />
        {vm.cashiers.length > 0 && (
          <ul aria-label="Кассиры кабинета">
            {vm.cashiers.map((cashier) => (
              <li key={cashier.id}>
                {cashier.name} — {cashier.state}
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="aqsi-panel" aria-labelledby="aqsi-check-title">
        <h3 id="aqsi-check-title">
          <ShieldCheck size={16} aria-hidden="true" /> Проверка фискального профиля
        </h3>
        <SelectField
          label="Касса"
          value={vm.check.deviceId === null ? '' : String(vm.check.deviceId)}
          options={vm.check.deviceOptions}
          onChange={(value) => actions.setCheckDevice(value === '' ? null : Number.parseInt(value, 10))}
        />
        <p className="aqsi-card__text">Отмечено товаров: {vm.check.goodCount}</p>
        {vm.check.blockedReason !== null && <p className="aqsi-card__text">{vm.check.blockedReason}</p>}
        <div className="aqsi-card__actions">
          <Button variant="primary" disabled={!vm.check.canRun} onClick={actions.runCheck}>
            Проверить
          </Button>
        </div>
        {vm.check.result !== null && (
          <div role="status" data-ready={vm.check.result.ready}>
            <p className="aqsi-card__state">
              {vm.check.result.ready ? (
                <CircleCheck size={14} aria-hidden="true" />
              ) : (
                <CircleAlert size={14} aria-hidden="true" />
              )}
              {vm.check.result.title}
            </p>
            <ul>
              {vm.check.result.lines.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </section>
  )
}
